import { supabase } from './supabaseService';
import type { BullhornSyncEntityType, BullhornSyncState } from './bullhornSyncQueries';

export type SyncRunMode = 'full' | 'incremental';
export type SyncRunStatus = 'running' | 'success' | 'partial' | 'error' | 'cancelled';
export type LogLevel = 'info' | 'success' | 'warn' | 'error' | 'pending';

export interface SyncRunRow {
  id: string;
  client_id: string;
  user_id?: string | null;
  mode: SyncRunMode;
  status: SyncRunStatus;
  entities: string[];
  total_discovered: number;
  total_synced: number;
  total_excluded: number;
  total_errors: number;
  started_at: string;
  completed_at?: string | null;
  summary?: Record<string, unknown>;
}

export interface SyncChangeRow {
  id: string;
  run_id: string;
  client_id: string;
  entity_type: string;
  bullhorn_id: string;
  display_name?: string | null;
  action: string;
  status: string;
  excluded: boolean;
  date_last_modified?: number | null;
  error_message?: string | null;
  metadata?: Record<string, unknown>;
}

export async function loadBullhornSyncState(clientId: string): Promise<BullhornSyncState> {
  const { data } = await supabase.from('clients').select('bullhorn_settings').eq('id', clientId).single();
  const settings = (data?.bullhorn_settings as Record<string, unknown>) || {};
  return (settings.syncState as BullhornSyncState) || {};
}

export async function saveBullhornSyncState(
  clientId: string,
  syncState: BullhornSyncState,
  existingSettings?: Record<string, unknown> | null
): Promise<void> {
  const { data } = await supabase.from('clients').select('bullhorn_settings').eq('id', clientId).single();
  const settings = existingSettings ?? ((data?.bullhorn_settings as Record<string, unknown>) || {});
  await supabase
    .from('clients')
    .update({ bullhorn_settings: { ...settings, syncState } })
    .eq('id', clientId);
}

export async function createSyncRun(params: {
  clientId: string;
  userId?: string;
  mode: SyncRunMode;
  entities: BullhornSyncEntityType[];
}): Promise<SyncRunRow> {
  const { data, error } = await supabase
    .from('bullhorn_sync_runs')
    .insert({
      client_id: params.clientId,
      user_id: params.userId ?? null,
      mode: params.mode,
      status: 'running',
      entities: params.entities,
      started_at: new Date().toISOString(),
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as SyncRunRow;
}

export async function finishSyncRun(
  runId: string,
  patch: Partial<Pick<SyncRunRow, 'status' | 'total_discovered' | 'total_synced' | 'total_excluded' | 'total_errors' | 'summary'>>
): Promise<void> {
  await supabase
    .from('bullhorn_sync_runs')
    .update({ ...patch, completed_at: new Date().toISOString() })
    .eq('id', runId);
}

export async function appendSyncLogLine(params: {
  runId: string;
  clientId: string;
  entityType: string;
  level: LogLevel;
  message: string;
  bullhornId?: string;
  localId?: string;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  const { error } = await supabase.from('bullhorn_sync_log_lines').insert({
    run_id: params.runId,
    client_id: params.clientId,
    entity_type: params.entityType,
    level: params.level,
    message: params.message,
    bullhorn_id: params.bullhornId ?? null,
    local_id: params.localId ?? null,
    metadata: params.metadata ?? {},
  });
  if (error) console.warn('[bullhornSyncStore] log line failed', error.message);
}

export async function insertSyncChanges(
  rows: Array<{
    runId: string;
    clientId: string;
    entityType: BullhornSyncEntityType;
    bullhornId: string;
    displayName: string;
    action: 'create' | 'update' | 'upsert';
    dateLastModified?: number | null;
    metadata?: Record<string, unknown>;
    excluded?: boolean;
  }>
): Promise<void> {
  if (rows.length === 0) return;
  const chunkSize = 200;
  for (let i = 0; i < rows.length; i += chunkSize) {
    const chunk = rows.slice(i, i + chunkSize);
    const { error } = await supabase.from('bullhorn_sync_changes').insert(
      chunk.map((r) => ({
        run_id: r.runId,
        client_id: r.clientId,
        entity_type: r.entityType,
        bullhorn_id: r.bullhornId,
        display_name: r.displayName,
        action: r.action,
        status: r.excluded ? 'excluded' : 'pending',
        excluded: Boolean(r.excluded),
        date_last_modified: r.dateLastModified ?? null,
        metadata: r.metadata ?? {},
      }))
    );
    if (error) throw error;
  }
}

export async function loadExclusionSet(clientId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('bullhorn_sync_exclusions')
    .select('entity_type, bullhorn_id')
    .eq('client_id', clientId);
  if (error) {
    console.warn('[bullhornSyncStore] exclusions load failed', error.message);
    return new Set();
  }
  return new Set((data || []).map((r) => `${r.entity_type}:${r.bullhorn_id}`));
}

export async function addExclusion(
  clientId: string,
  entityType: BullhornSyncEntityType,
  bullhornId: string,
  userId?: string,
  reason?: string
): Promise<void> {
  await supabase.from('bullhorn_sync_exclusions').upsert({
    client_id: clientId,
    entity_type: entityType,
    bullhorn_id: bullhornId,
    reason: reason ?? 'Handmatig uitgesloten',
    created_by: userId ?? null,
    created_at: new Date().toISOString(),
  });
}

export async function removeExclusion(
  clientId: string,
  entityType: BullhornSyncEntityType,
  bullhornId: string
): Promise<void> {
  await supabase
    .from('bullhorn_sync_exclusions')
    .delete()
    .eq('client_id', clientId)
    .eq('entity_type', entityType)
    .eq('bullhorn_id', bullhornId);
}

export async function getRunChanges(runId: string): Promise<SyncChangeRow[]> {
  const { data, error } = await supabase
    .from('bullhorn_sync_changes')
    .select('*')
    .eq('run_id', runId)
    .order('entity_type')
    .order('display_name');
  if (error) throw error;
  return (data || []) as SyncChangeRow[];
}

export async function updateChangeExcluded(changeId: string, excluded: boolean): Promise<void> {
  await supabase
    .from('bullhorn_sync_changes')
    .update({
      excluded,
      status: excluded ? 'excluded' : 'pending',
    })
    .eq('id', changeId);
}

export async function markChangeSynced(changeId: string, errorMessage?: string): Promise<void> {
  await supabase
    .from('bullhorn_sync_changes')
    .update({
      status: errorMessage ? 'error' : 'synced',
      error_message: errorMessage ?? null,
      synced_at: new Date().toISOString(),
    })
    .eq('id', changeId);
}

export async function searchSyncRuns(clientId: string, search: string, limit = 30): Promise<SyncRunRow[]> {
  let q = supabase
    .from('bullhorn_sync_runs')
    .select('*')
    .eq('client_id', clientId)
    .order('started_at', { ascending: false })
    .limit(limit);
  const { data, error } = await q;
  if (error) throw error;
  const runs = (data || []) as SyncRunRow[];
  if (!search.trim()) return runs;
  const s = search.toLowerCase();
  return runs.filter(
    (r) =>
      r.mode.toLowerCase().includes(s) ||
      r.status.toLowerCase().includes(s) ||
      (r.entities || []).some((e) => e.toLowerCase().includes(s))
  );
}

export async function searchSyncLogLines(
  clientId: string,
  search: string,
  runId?: string,
  limit = 500
): Promise<
  Array<{
    id: string;
    run_id: string;
    created_at: string;
    entity_type: string;
    level: string;
    message: string;
    bullhorn_id?: string | null;
  }>
> {
  let q = supabase
    .from('bullhorn_sync_log_lines')
    .select('id, run_id, created_at, entity_type, level, message, bullhorn_id')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (runId) q = q.eq('run_id', runId);
  const { data, error } = await q;
  if (error) throw error;
  const rows = data || [];
  if (!search.trim()) return rows;
  const s = search.toLowerCase();
  return rows.filter(
    (r) =>
      r.message?.toLowerCase().includes(s) ||
      r.entity_type?.toLowerCase().includes(s) ||
      r.bullhorn_id?.toLowerCase().includes(s)
  );
}
